import { ampli } from '@/ampli';
import { useLayoutEffect, useRef, useState } from 'react';

import { TokenContent } from '@/lib/api/tokens/types';

import { TextSkeleton } from '@/components/Skeleton';

import { BRIDGED_USDC_PER_CHAIN_ID, NATIVE_USDC_PER_CHAIN_ID } from '@/constants/addresses';
import { NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { ZoraToken } from '@/hooks/useZora';

import { fontStyles } from '@/styles/typography';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { CoinGeckoCoinDataByTokenAddressResponse } from '@/utils/coingecko/types';

import { TokenProfileAdditionalLinks } from '../TokenProfileAdditionalLinks';
import { TokenProfileRelatedTokenShortcut } from '../TokenShortcut';

interface Props {
  token?: TokenResult;
  data?: CoinGeckoCoinDataByTokenAddressResponse | null;
  isLoading: boolean;
  tokenContent?: TokenContent;
  zora?: ZoraToken;
}

const DEFAULT_ABOUT_TEXT = 'No information found…';

export const TokenProfileAbout = ({ token, data, isLoading, tokenContent, zora }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const descriptionRef = useRef<HTMLSpanElement>(null);

  const aboutText = tokenContent?.about || data?.description.en || DEFAULT_ABOUT_TEXT;
  const isBridgedUsdc = token != null && token.address === BRIDGED_USDC_PER_CHAIN_ID[token.chainId];

  useLayoutEffect(() => {
    if (isLoading || isExpanded) return;

    const description = descriptionRef.current;
    if (!description) return;

    const updateTruncation = () =>
      setIsTruncated(description.scrollHeight > description.clientHeight + 1);

    updateTruncation();
    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(description);

    return () => resizeObserver.disconnect();
  }, [isLoading, aboutText, isExpanded]);

  function handleShowMore() {
    setIsExpanded(!isExpanded);

    // token_address is required on the event: skip tracking until the token
    // resolves rather than sending a placeholder.
    if (token) {
      ampli.tokenDescriptionExpanded({
        token_address: token.address,
        token_chain_id: token.chainId,
        token_chain_name: NETWORK_NAME_PER_CHAIN_ID[token.chainId],
        token_symbol: token.symbol || '',
      });
    }
  }

  return (
    <div data-state={isExpanded ? 'open' : 'closed'}>
      <h2
        className={fontStyles({ variant: 'H5', class: 'flex px-6 tablet:px-8 pt-6 tablet:pt-8' })}
      >
        About {tokenContent?.displayName ?? token?.name}
      </h2>
      {isLoading && (
        <div className="flex flex-col gap-2 pb-4 tablet:pb-6 pt-6 px-6 tablet:px-8">
          <TextSkeleton width="100%" height={14} lines={2} />
          <TextSkeleton width="70%" height={14} />
        </div>
      )}
      {!isLoading && (
        <div className="flex flex-col">
          <div
            className={fontStyles({
              variant: 'BODY_LG',
              class: 'text-token-gray-800 m-0 pb-4 tablet:pb-6 pt-6 px-6 tablet:px-8',
            })}
          >
            <span
              ref={descriptionRef}
              dangerouslySetInnerHTML={{ __html: aboutText }}
              className={classNames('token-about-html', { 'line-clamp-3': !isExpanded })}
            />
            {isBridgedUsdc && token && isExpanded && (
              <div className={classNames('flex flex-col gap-3 py-4')}>
                <h5 className={fontStyles({ variant: 'BODY_LG' })}>
                  Switch to USDC issued by Circle on {NETWORK_NAME_PER_CHAIN_ID[token.chainId]}
                </h5>
                <TokenProfileRelatedTokenShortcut
                  title="USD Coin"
                  subtitle="USDC"
                  chainId={token.chainId}
                  tokenAddress={NATIVE_USDC_PER_CHAIN_ID[token.chainId]}
                />
              </div>
            )}
            {isTruncated && (
              <button
                type="button"
                className={classNames('text-token-gray-1600 mt-2')}
                onClick={handleShowMore}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
          <div className="border-t border-token-gray-200 px-6 py-5 tablet:px-8 tablet:py-6">
            <TokenProfileAdditionalLinks
              token={token}
              coingeckoLinks={data?.links}
              tokenContentLinks={tokenContent?.links}
              zora={zora}
            />
          </div>
        </div>
      )}
    </div>
  );
};
